import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { guardarEstado } from '@/lib/estadoDeSessao';
import { canalDaConversa } from '@/conversas/canais';
import { ETAPAS_PADRAO, PAPEIS_NUMERO, TEMPERATURAS, TIPOS_INTERACAO, etapaPorId, RESULTADOS_LIGACAO } from '@/lib/constants';
import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { buscarConversaDoLead, iniciarConversa, ouvirNumeros } from '@/services/conversasService';
import NomeEditavel from '@/components/NomeEditavel';
import { podeCorrigirValores } from '@/leads/valores';
import { procedimentosDaClinica } from '@/leads/inferencia';
import { exigeCampoExtra, mostrarBadgeRetorno } from '@/leads/regras';
import { motivoPorId } from '@/leads/motivos';
import { ehSemOrigem, origemPorId, origensAtivas } from '@/leads/origens';
import { FASES, secaoDaEtapa } from '@/leads/ficha';
import { apenasNegociadores, apenasSdrs, nomeSdrDaPlanilha, opcoesComGravado } from '@/equipe/funcoes';
import BlocoTarefas from '@/components/leads/BlocoTarefas';
import BlocoAgendamento from '@/components/leads/BlocoAgendamento';
import BlocoPosse from '@/components/leads/BlocoPosse';
import BlocoRecebimentos from '@/components/leads/BlocoRecebimentos';
import CampoValorOrcado from '@/components/leads/CampoValorOrcado';
import DialogoValores from '@/components/leads/DialogoValores';
import { tratarErro } from '@/lib/erros';
import {
  adicionarNota,
  atualizarLead,
  formatarTelefone,
  moverEtapa,
  ouvirTimeline,
  registrarTentativa,
  corrigirValores,
  renomearLead,
} from '@/services/leadsService';

const moeda = (v) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function quando(valor) {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  if (!data) return 'agora';
  return format(data, "d 'de' MMM',' HH:mm", { locale: ptBR });
}


const MENSAGENS_INICIAR = {
  'functions/invalid-argument': 'Este lead não tem um celular válido para WhatsApp.',
  // ★ A-8.1: "da Evolution" → "de Vendas".
  'functions/failed-precondition': 'O número de Vendas não permite iniciar conversa agora.',
  'functions/resource-exhausted': 'Aguarde: você já iniciou muitas conversas nesta hora — o WhatsApp bane número que dispara.',
  'functions/permission-denied': 'Seu papel não atende a caixa Conversas — Leads.',
  'functions/not-found': 'Lead não encontrado.',
  'functions/unavailable': 'Não foi possível falar com o servidor. Tente de novo.',
};

/**
 * Uma fase da ficha: `<details>` nativo (recolhível sem estado global), com a cor
 * fixa da fase e a borda mais forte quando é a fase da etapa atual.
 */
function Fase({ fase, atual, abertaInicial, children }) {
  const [aberta, setAberta] = useState(abertaInicial);
  return (
    <details
      className={atual ? 'fase fase-atual' : 'fase'}
      data-fase={fase.id}
      style={{ '--cor-fase': fase.cor }}
      open={aberta}
      onToggle={(e) => setAberta(e.currentTarget.open)}
    >
      <summary>{fase.label}</summary>
      <div className="fase-corpo">{children}</div>
    </details>
  );
}

/**
 * ★ VX — A FICHA ÚNICA DO LEAD, na ordem do trabalho (decisão do dono, 20/set/2026).
 *
 * É a mesma ficha no funil, na lista (`PainelLead`) e no chat (`PainelContato`): os
 * hosts são cascas que só decidem de onde vem o lead. Por isso ela NÃO depende de
 * provider nenhum — recebe tudo por props — e NÃO monta diálogo: emite `aoPedirEtapa`
 * e cada host monta o seu `DialogoEtapa`.
 *
 *   CABEÇALHO (sempre): nome com lápis + lembrete "confira se está como no {sistema}" ·
 *     telefone · Código no {sistema} · pílula + select de Etapa · Abrir/Iniciar conversa · atalho Nota
 *   ① Captação — SDR · ② Agendamento e avaliação · ③ Negociação e fechamento — negociador
 *   NOTA (a entrada da timeline) · TIMELINE
 *
 * Cor fixa por fase; a fase da etapa ATUAL tem a borda mais forte e começa aberta.
 * `{sistema}` é `nomeDoSistema(clinica.config)` — nenhuma marca fixa (VX complemento).
 *
 * @param conversa  quando a ficha está DENTRO de uma conversa (chat), o botão de
 *                  abrir/iniciar conversa some — já se está nela.
 * @param abrirTodas no funil/lista as três fases começam abertas; no chat, só a atual.
 */
export default function FichaLead({
  lead,
  clinicaId,
  autor,
  equipe = [],
  clinica = null,
  podeLancar = false,
  aoPedirEtapa,
  conversa = null,
  abrirTodas = true,
  // ★ WD: o PAPEL de quem está vendo, por PROP. A ficha não chama `useAuth` de propósito (guarda da VX:
  // ela vive só de props), então quem a monta informa — `PainelLead` e `PainelContato`.
  papel = null,
}) {
  const navegar = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [nota, setNota] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  // Conversa de WhatsApp vinculada a este lead (null = nenhuma; undefined = ainda
  // procurando). A busca mora no serviço (VM) — o teste de tela a dubla.
  const [conversaDoLead, setConversaDoLead] = useState(undefined);
  /**
   * ★★★ WT lote 1 — "NÃO ACHEI" É DIFERENTE DE "NÃO EXISTE", e esta ficha é o caso de
   * origem de toda a peça.
   *
   * ⚠⚠ O `catch` abaixo caía em `setConversaDoLead(null)`, e `null` significava *"o lead
   * nunca falou com a clínica"*. ★ Uma consulta **recusada** produzia a mesma coisa que uma
   * consulta vazia — e a tela então **afirmava** algo falso sobre uma paciente com 50
   * mensagens gravadas. Foi assim que o defeito da WC durou sete semanas.
   *
   * ★ Agora há TRÊS estados, e cada um tem a sua frase:
   *   `undefined` → ainda procurando;
   *   `null` + `falhou=false` → respondeu VAZIO: *"nunca falou com a clínica"* (verdade);
   *   `null` + `falhou=true`  → **não deu para verificar** — sem afirmar inexistência.
   */
  const [falhouAConversa, setFalhouAConversa] = useState(false);
  const [abrindoConversa, setAbrindoConversa] = useState(false);
  const [numeroResgate, setNumeroResgate] = useState(null);
  const refNota = useRef(null);
  // ★ WD: o diálogo de correção de valores (dinheiro) — a decisão de PODER abrir vive em leads/valores.js.
  const [corrigindoValores, setCorrigindoValores] = useState(false);

  const noChat = Boolean(conversa);

  useEffect(() => {
    setConversaDoLead(undefined);
    setFalhouAConversa(false);
    if (!lead || noChat) return undefined;
    let cancelado = false;
    buscarConversaDoLead(clinicaId, lead.id)
      .then((c) => { if (!cancelado) setConversaDoLead(c); })
      .catch((e) => {
        /**
         * ★ WC — DECISÃO: o `catch` FICA, com o log.
         *
         * Ele foi o que transformou um erro de permissão em AUSÊNCIA: o botão "Abrir conversa" sumia em
         * silêncio para toda SDR, e por isso ninguém reportou em sete semanas. A tentação é passar a
         * mostrar o erro — mas esta busca é acessória: se ela falhar, a ficha inteira continua útil, e um
         * alerta vermelho por algo que não impede o trabalho treina a equipe a ignorar alertas.
         *
         * O que torna a ausência DETECTÁVEL não é mostrar o erro: é o teste que vê o botão APARECER
         * (`web/src/pages/wc.espacoNaConsulta.render.test.js`, "★★ PELA TELA: … mostra 'Abrir conversa'").
         * Sem ele, o botão volta a sumir sem aviso na próxima mudança.
         */
        // ★★ WT: o registro vai para `clinicas/{id}/erros` e o superadmin passa a ver a
        // recusa de permissão que antes virava "nunca falou".
        tratarErro('a conversa do lead', null, 'service')(e);
        if (!cancelado) { setConversaDoLead(null); setFalhouAConversa(true); }
      });
    return () => {
      cancelado = true;
    };
  }, [clinicaId, lead?.id, noChat]);

  useEffect(() => {
    if (!clinicaId || noChat) return undefined;
    return ouvirNumeros(
      clinicaId,
      (lista) => setNumeroResgate(lista.find((n) => n.papel === PAPEIS_NUMERO.marketing_resgate.id) ?? null),
      () => setNumeroResgate(null),
    );
  }, [clinicaId, noChat]);

  useEffect(() => {
    setTimeline([]);
    setErro(null);
    if (!lead) return undefined;
    return ouvirTimeline(clinicaId, lead.id, setTimeline, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir timeline:', e);
      setErro('Não foi possível carregar a timeline.');
    });
  }, [clinicaId, lead?.id]);

  // O código do sistema da clínica, editável no cabeçalho (mesmo campo de sempre).
  useEffect(() => {
    setCodigo(lead?.codigoCareflow ?? '');
  }, [lead?.id, lead?.codigoCareflow]);

  if (!lead) return null;

  /**
   * Abre o inbox numa conversa. A seleção é estado de sessão por canal
   * (`inbox:selecionada:<canal>`); gravar a chave ANTES de navegar faz o
   * `ConversasProvider` nascer com a conversa aberta.
   */
  function irParaConversa(c) {
    const canal = canalDaConversa(c);
    guardarEstado(`inbox:selecionada:${canal}`, c.id);
    navegar(`/conversas/${canal}`);
  }

  /**
   * ★ VM — o botão SEMPRE funciona: com conversa vinculada, abre; sem, INICIA uma
   * pelo número de resgate e só então navega. Não envia mensagem nenhuma. Só existe
   * aqui, na ficha de UM lead (guarda em `vm.guardas.test`).
   */
  async function abrirOuIniciarConversa() {
    if (conversaDoLead) {
      irParaConversa(conversaDoLead);
      return;
    }
    setErro(null);
    setAbrindoConversa(true);
    try {
      const { data } = await iniciarConversa({
        clinicaId,
        papel: PAPEIS_NUMERO.marketing_resgate.id,
        leadId: lead.id,
      });
      /**
       * ★★★ WS — O ESPAÇO VEM DO SERVIDOR. Antes esta linha era:
       *
       *     const nova = { id: data.conversaId, espaco: 'vendas', numeroPapel: … };
       *
       * ⚠⚠ `'vendas'` era um literal — o cliente AFIRMAVA onde a conversa estava sem saber.
       * E a afirmação podia ser falsa: `garantirConversa` deduplica por telefone+número e
       * devolve a conversa que já existe **sem** reescrever o espaço dela. Se alguém a
       * transferiu para a recepção, o canal calculado aqui saía errado, a seleção era
       * gravada no canal errado e a tela abria vazia (`docs/medicao-conversa-ficha.md`).
       *
       * ★★ E a falta do campo é FALHA EXPLÍCITA, não reserva para `'vendas'`. ⚠ Um
       * `?? 'vendas'` aqui reintroduziria o defeito exatamente nos casos em que o servidor
       * não soube responder — e calado. Melhor a pessoa ver um erro e achar a conversa pela
       * lista do que ser levada a um canal onde ela não está.
       */
      if (!data?.espaco) {
        throw new Error('O servidor não informou em qual caixa a conversa está. Procure-a na lista de conversas.');
      }
      const nova = { id: data.conversaId, espaco: data.espaco, numeroPapel: data.numeroPapel ?? null };
      setConversaDoLead(nova);
      irParaConversa(nova);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao iniciar conversa pela ficha:', e);
      setErro(MENSAGENS_INICIAR[e.code] ?? e.message ?? 'Não foi possível iniciar a conversa.');
    } finally {
      setAbrindoConversa(false);
    }
  }

  /** ★ VM — renomear pela ficha: `lead.nome` E o nome salvo das conversas vinculadas (serviço). */
  async function salvarNome(novo) {
    await renomearLead(clinicaId, lead, novo, autor);
  }

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  function mudarEtapa(novaEtapa) {
    if (!novaEtapa || novaEtapa === lead.etapa) return;
    // Etapas com campo obrigatório são coletadas em diálogo próprio — pelo HOST.
    if (exigeCampoExtra(novaEtapa, lead)) {
      aoPedirEtapa({ lead, etapa: novaEtapa });
      return;
    }
    executar(() => moverEtapa(clinicaId, lead, novaEtapa, {}, autor));
  }

  /** O Código do sistema da clínica grava o MESMO campo de sempre — só mudou de lugar na tela. */
  function salvarCodigo() {
    const novo = codigo.trim();
    if (novo === (lead.codigoCareflow ?? '')) return;
    executar(() => atualizarLead(clinicaId, lead.id, { codigoCareflow: novo, registrarNaTimeline: false }, autor));
  }


  // VP: SDR e negociador são FUNÇÕES (equipe/funcoes), listas distintas; o já gravado
  // fora da lista continua visível como "(fora da função)". Importado: SDR só como texto.
  const sdrs = opcoesComGravado(apenasSdrs(equipe), lead.sdrId, lead.sdrNome);
  const negociadores = opcoesComGravado(apenasNegociadores(equipe), lead.negociadorId, lead.negociadorNome);
  const sdrDaPlanilha = nomeSdrDaPlanilha(lead);
  const faltaNegociador = mostrarBadgeRetorno(lead) && !lead.negociadorId;

  const etapa = etapaPorId(lead.etapa);
  const faseAtual = secaoDaEtapa(lead.etapa);
  const sistema = nomeDoSistema(clinica?.config);
  const fechou = lead.etapa === 'fechado' || Number(lead.valorFechado) > 0;

  return (
    <div className="ficha-lead" key={lead.id}>
      {/* ---------------------------------------------------------------- CABEÇALHO */}
      <header className="ficha-cabecalho">
        <div className="ficha-nome">
          <NomeEditavel
            key={lead.id}
            id="pl-nome"
            nome={lead.nome || 'Sem nome'}
            valorInicial={lead.nome ?? ''}
            rotulo="Nome do lead"
            ajuda="O nome muda no funil, na lista e na conversa vinculada."
            permiteVazio={false}
            titulo="Editar o nome deste lead"
            aoSalvar={salvarNome}
          />
          {/* Decisão do dono: o lembrete aparece SEMPRE — o nome tem de bater com o do sistema da clínica. */}
          <p className="ficha-lembrete" role="note">confira se está como no {sistema}</p>
          <p className="painel-sub">
            {origemPorId(clinica?.config, lead.origem).label}
            {lead.procedimento ? ` · ${lead.procedimento}` : ''}
            {lead.procedimento && lead.anuncioInferido === true && (
              <span className="pilula-inferido" title="Preenchido pela primeira mensagem do WhatsApp — confira">
                inferido
              </span>
            )}
          </p>
        </div>

        <dl className="dados-lead">
          {/* ★ WC-c1: o telefone voltou a ser SOMENTE LEITURA. Decisão do dono: ele já entra correto, vindo
              do WhatsApp. E o técnico confirma — o telefone é a CHAVE que liga o lead à conversa
              (`resgate__+55…`); um dígito errado à mão faz lead e conversa deixarem de casar, que é pior do
              que o problema que o campo resolvia. Paciente que TROCA de número não se resolve reescrevendo o
              cadastro: ela escreve do número novo, e isso abre conversa nova. */}
          {lead.telefone && (
            <>
              <dt>Telefone</dt>
              <dd>
                <a href={`tel:${lead.telefone}`}>{formatarTelefone(lead.telefone)}</a>
              </dd>
            </>
          )}
          {lead.email && (
            <>
              <dt>E-mail</dt>
              <dd>{lead.email}</dd>
            </>
          )}
          {lead.cidade && (
            <>
              <dt>Cidade</dt>
              <dd>{lead.cidade}</dd>
            </>
          )}
        </dl>

        <div className="campo campo-codigo">
          <label htmlFor="pl-codigo">Código no {sistema}</label>
          <input
            id="pl-codigo"
            value={codigo}
            disabled={ocupado}
            placeholder="número do paciente"
            onChange={(e) => setCodigo(e.target.value)}
            onBlur={salvarCodigo}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); salvarCodigo(); } }}
          />
        </div>

        <div className="linha-pilula-acao">
          <span className="pilula" style={{ background: etapa.cor }}>
            {etapa.label}
          </span>
          <div className="campo campo-etapa">
            <label htmlFor="pl-etapa">Etapa</label>
            <select id="pl-etapa" value={lead.etapa} disabled={ocupado} onChange={(e) => mudarEtapa(e.target.value)}>
              {ETAPAS_PADRAO.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </select>
          </div>
          {!noChat && (
            /* ★ VM — sempre habilitado quando há telefone: abre a conversa que existe
               ou inicia uma nova pelo número de resgate. */
            <button
              type="button"
              className="btn-secundario compacto"
              onClick={abrirOuIniciarConversa}
              disabled={conversaDoLead === undefined || abrindoConversa || !lead.telefone}
              title={
                conversaDoLead === undefined
                  ? 'Procurando a conversa…'
                  : conversaDoLead
                    ? 'Abrir a conversa de WhatsApp deste lead no inbox'
                    : !lead.telefone
                      ? 'Este lead não tem telefone'
                      : `Iniciar conversa no WhatsApp pelo número de Vendas${numeroResgate?.telefone ? ` (${formatarTelefone(numeroResgate.telefone)})` : ''}`
              }
            >
              {/* ★★★ WT lote 1b — "PROCURANDO CONVERSA…" enquanto a busca não resolveu.
                  ⚠⚠ O rótulo era `conversaDoLead ? 'Abrir' : 'Iniciar'` — e `undefined` (ainda
                  buscando) caía no `else`, mostrando **"Iniciar conversa"**. ★ O botão já
                  estava desabilitado, mas o RÓTULO afirmava algo que podia ser falso: no caso
                  da Dalva (06/out) a conversa EXISTIA, o dono leu "Iniciar", clicou depois da
                  resolução e relatou o caso errado — o que custou uma rodada de medição. */}
              {abrindoConversa
                ? 'Abrindo…'
                : conversaDoLead === undefined
                  ? 'Procurando conversa…'
                  : conversaDoLead ? 'Abrir conversa' : 'Iniciar conversa'}
            </button>
          )}
          <button
            type="button"
            id="pl-atalho-nota"
            className="btn-texto"
            title="Ir para o campo de nota"
            onClick={() => { refNota.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); refNota.current?.focus?.(); }}
          >
            Nota
          </button>
        </div>
        {/* ★★ WS — este aviso agora é VERDADE. Antes `buscarConversaDoLead` filtrava
            `espaco == 'vendas'`, e o aviso aparecia sobre conversas de 50 mensagens que
            tinham sido transferidas para a recepção. ★ A busca passou a olhar TODOS os
            espaços pelo `leadId`, então "nunca falou" só aparece quando não há conversa em
            espaço nenhum. */}
        {/* ★★★ WT — a frase SÓ quando a consulta respondeu VAZIO. ⚠ Com ela falhada, a
            afirmação de inexistência sai de cena e entra o que de fato se sabe. */}
        {!noChat && conversaDoLead === null && lead.telefone && !falhouAConversa && (
          <p className="aviso-primeiro-contato" role="note">
            Este lead nunca falou com a clínica pelo sistema. A conversa abre vazia, pelo número de
            Vendas; escreva você mesmo — não é disparo.
          </p>
        )}
        {!noChat && falhouAConversa && lead.telefone && (
          <p className="aviso-primeiro-contato" role="alert">
            Não foi possível verificar as conversas deste lead. Recarregue a página. Se continuar,
            avise o suporte.
          </p>
        )}
        {erro && <p className="erro">{erro}</p>}
      </header>

      {/* ---------------------------------------------------------------- ① CAPTAÇÃO — SDR */}
      <Fase fase={FASES[0]} atual={faseAtual === 'captacao'} abertaInicial={abrirTodas || faseAtual === 'captacao'}>
        <div className="campo">
          <label htmlFor="pl-origem">
            Origem
            {lead.origemInferida === true && (
              <span className="pilula-inferido" title="Deduzida da primeira mensagem do WhatsApp — confira">inferido</span>
            )}
          </label>
          <select
            id="pl-origem"
            value={ehSemOrigem(lead.origem) ? '' : lead.origem}
            disabled={ocupado}
            onChange={(e) =>
              executar(() =>
                atualizarLead(clinicaId, lead.id, { origem: e.target.value, origemInferida: false, registrarNaTimeline: false }, autor),
              )
            }
          >
            <option value="">— escolha —</option>
            {!ehSemOrigem(lead.origem) && !origensAtivas(clinica?.config).some((o) => o.id === lead.origem) && (
              <option value={lead.origem}>{origemPorId(clinica?.config, lead.origem).label}</option>
            )}
            {origensAtivas(clinica?.config).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="linha">
          <div className="campo">
            <label htmlFor="pl-proc">
              Procedimento
              {lead.anuncioInferido === true && <span className="pilula-inferido">inferido</span>}
            </label>
            <select
              id="pl-proc"
              value={lead.procedimento ?? ''}
              disabled={ocupado}
              onChange={(e) =>
                executar(() =>
                  atualizarLead(clinicaId, lead.id, { procedimento: e.target.value, anuncioInferido: false, registrarNaTimeline: false }, autor),
                )
              }
            >
              <option value="">Não informado</option>
              {lead.procedimento && !procedimentosDaClinica(clinica?.config).includes(lead.procedimento) && (
                <option value={lead.procedimento}>{lead.procedimento}</option>
              )}
              {procedimentosDaClinica(clinica?.config).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="pl-temp">Temperatura</label>
            <select
              id="pl-temp"
              value={lead.temperatura ?? 'morno'}
              disabled={ocupado}
              onChange={(e) =>
                executar(() => atualizarLead(clinicaId, lead.id, { temperatura: e.target.value, registrarNaTimeline: false }, autor))
              }
            >
              {TEMPERATURAS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ★★★ PC-6 P6: a posse do lead — SDR sem select (texto + "Assumir lead"); gestor com o select
            pela callable `assumirLead`. As rules fecham a escrita direta para todos. */}
        <BlocoPosse
          lead={lead}
          clinicaId={clinicaId}
          papel={papel}
          usuarioId={autor?.id ?? null}
          equipe={equipe}
          opcoes={sdrs}
          disabled={ocupado}
          rotuloExtra={sdrDaPlanilha ? (
            <span className="pilula-inferido" title="Nome do SDR na planilha importada — não é um usuário do sistema">
              planilha: {sdrDaPlanilha}
            </span>
          ) : null}
        />

        <div className="acoes-rapidas">
          <span className="rotulo-acoes">
            Registrar tentativa
            <span className="contador-tentativas" title="Tentativas de contato registradas">{lead.tentativasContato ?? 0}</span>
          </span>
          <div className="botoes-tentativa">
            {RESULTADOS_LIGACAO.map((r) => (
              <button
                key={r}
                type="button"
                className="btn-secundario"
                disabled={ocupado}
                onClick={() => executar(() => registrarTentativa(clinicaId, lead, r, autor))}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </Fase>

      {/* ---------------------------------------------------------------- ② AGENDAMENTO E COMPARECIMENTO */}
      <Fase fase={FASES[1]} atual={faseAtual === 'agenda'} abertaInicial={abrirTodas || faseAtual === 'agenda'}>
        {/* VQ-2: "Agendar" abre o MESMO DialogoEtapa do funil (grade de horários), pelo host. */}
        {/* ★★ WO lote 3c: `equipe` desce porque o diálogo da baixa passou a pedir o
            negociador; `autor` já identifica quem está logado, para o padrão "eu". */}
        <BlocoAgendamento lead={lead} clinicaId={clinicaId} autor={autor} clinica={clinica} equipe={equipe} aoAgendar={(l) => aoPedirEtapa({ lead: l, etapa: 'agendado' })} />
        <BlocoTarefas lead={lead} clinicaId={clinicaId} autor={autor} clinica={clinica} />
        {/* VG: o motivo da coluna "Não agendou", legível mesmo se o motivo tiver sido inativado depois. */}
        {lead.etapa === 'nao_agendou' && lead.motivoNaoAgendou && (
          <dl className="dados-lead">
            <dt>Motivo</dt>
            <dd>{motivoPorId(clinica?.config, 'nao_agendou', lead.motivoNaoAgendou).rotulo}{lead.desfechoNegativoEm ? ' — encerra' : ''}</dd>
          </dl>
        )}
      </Fase>

      {/* ---------------------------------------------------------------- ③ NEGOCIAÇÃO E FECHAMENTO — negociador */}
      <Fase fase={FASES[2]} atual={faseAtual === 'fechamento'} abertaInicial={abrirTodas || faseAtual === 'fechamento'}>
        {/* ★ WD-c2 (item 4): `alinha-caixinhas` — o select do negociador e a caixinha da oportunidade
            começam na mesma altura mesmo quando a pílula "informe" joga o rótulo para a 2ª linha. */}
        <div className="linha alinha-caixinhas">
          {/* V4: segundo responsável. Quem compareceu e não fechou é trabalhado pelo negociador. */}
          <div className="campo">
            <label htmlFor="pl-negociador">
              Negociador
              {faltaNegociador && <span className="pilula-inferido alerta">defina</span>}
            </label>
            <select
              id="pl-negociador"
              value={lead.negociadorId ?? ''}
              disabled={ocupado}
              onChange={(e) => {
                const n = negociadores.find((u) => u.id === e.target.value);
                executar(() =>
                  atualizarLead(clinicaId, lead.id, { negociadorId: n?.id ?? null, negociadorNome: n?.nome ?? '', registrarNaTimeline: false }, autor),
                );
              }}
            >
              <option value="">Sem negociador</option>
              {negociadores.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>
          </div>
          {/* VE: a oportunidade — quem vai negociar, e por quanto.
              ★★ WD-c2: SÓ ENQUANTO NÃO FECHOU. Depois de fechado ela é uma das cinco linhas do bloco de
              leitura logo abaixo, e muda pelo "Editar" — nunca as duas coisas ao mesmo tempo. */}
          {!fechou && (
            <CampoValorOrcado lead={lead} clinicaId={clinicaId} autor={autor} disabled={ocupado} id="pl-valor-orcado" />
          )}
        </div>

        {lead.etapa === 'nao_fechou' && lead.motivoNaoFechou && (
          <dl className="dados-lead">
            <dt>Motivo</dt>
            <dd>{motivoPorId(clinica?.config, 'nao_fechou', lead.motivoNaoFechou).rotulo}{lead.desfechoNegativoEm ? ' — encerra' : ''}</dd>
          </dl>
        )}

        {/* ★★ WD-c2 — O BLOCO É DE LEITURA. O único campo editável aqui é o select do Negociador, acima.
            Os cinco valores viram TEXTO, e mudam pelo botão "Editar", que abre o MESMO conjunto de campos
            que aparece ao mover o lead para "Fechou" (`CamposValores`). Dois momentos, uma tela.
            Antes havia caixinhas espalhadas — oportunidade aqui, entrada/código/nome logo abaixo — e
            nenhuma delas dizia o que ia acontecer ao sair do campo. */}
        {fechou && (
          <dl className="dados-lead" id="pl-valores-leitura">
            <dt>Valor de oportunidade</dt>
            <dd>{moeda(lead.valorOrcado)}</dd>
            <dt>Total da venda</dt>
            <dd>{moeda(lead.valorFechado)}</dd>
            <dt>Entrada paga no ato</dt>
            <dd>{moeda(lead.valorEntrada)}</dd>
            <dt>Código Paciente</dt>
            <dd>{lead.codigoCareflow || '—'}</dd>
            {/* ★ O nome APARECE aqui como informação, e é editado na janela e no topo da ficha — é o MESMO
                campo (`nome`), em dois lugares visíveis, nenhum escondido. */}
            <dt>Nome no {sistema}</dt>
            <dd>{lead.nome || '—'}</dd>
          </dl>
        )}
        {/* ★ Quem decide é `podeCorrigirValores` — etapa, papel e (no futuro) mês fechado. A tela não
            compara papel nem etapa por conta própria; há guarda de fonte que cobra isso. */}
        {podeCorrigirValores(lead, { papel }) && (
          <button
            type="button"
            id="pl-corrigir-valores"
            className="btn-secundario"
            disabled={ocupado}
            onClick={() => setCorrigindoValores(true)}
          >
            Editar
          </button>
        )}
        {/* Recebimentos (saldo, barra, lançamentos; o gestor lança) — o bloco se esconde sozinho sem valorFechado. */}
        <BlocoRecebimentos lead={lead} clinicaId={clinicaId} podeLancar={podeLancar} config={clinica?.config ?? null} />
      </Fase>

      {corrigindoValores && (
        <DialogoValores
          lead={lead}
          clinica={clinica}
          aoFechar={() => setCorrigindoValores(false)}
          aoSalvar={async (dados) => {
            await corrigirValores(clinicaId, lead, dados, autor);
            setCorrigindoValores(false);
          }}
        />
      )}

      {/* ---------------------------------------------------------------- NOTA (a entrada da timeline) */}
      <form
        className="form-nota"
        onSubmit={(e) => {
          e.preventDefault();
          const texto = nota;
          setNota('');
          executar(() => adicionarNota(clinicaId, lead.id, texto, autor));
        }}
      >
        <label htmlFor="pl-nota">Nota</label>
        <textarea
          id="pl-nota"
          ref={refNota}
          rows={2}
          placeholder="O que aconteceu neste contato?"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
        <button type="submit" className="btn-primario" disabled={ocupado || !nota.trim()}>
          Adicionar à timeline
        </button>
      </form>

      {/* ---------------------------------------------------------------- TIMELINE */}
      <section className="timeline">
        <h3>Timeline</h3>
        {timeline.length === 0 && <p className="vazio">Nenhuma interação ainda.</p>}
        <ol>
          {timeline.map((evento) => (
            <li key={evento.id} className={`evento evento-${evento.tipo}`}>
              <div className="evento-topo">
                <span className="evento-tipo">{TIPOS_INTERACAO[evento.tipo] ?? evento.tipo}</span>
                <time>{quando(evento.criadoEm)}</time>
              </div>
              <p>{evento.texto}</p>
              <span className="evento-autor">{evento.autorNome}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
